"use client";

import React, { useEffect } from "react";
import { TermsContent } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";

interface TermsContentProps {
  blok?: TermsContent & SbBlokData;
}

const PartnerTermsContent: React.FC<TermsContentProps> = ({ blok }) => {
  useEffect(() => {
    const handleScroll = () => {
      const tocLinks = document.querySelectorAll<HTMLAnchorElement>(".terms-conditions-toc-links ol > li > a");

      tocLinks.forEach((link) => {
        const href = link.getAttribute("href");
        if (!href) return;

        const target = document.querySelector<HTMLElement>(href);
        if (!target) return;

        const coords = target.getBoundingClientRect();

        if (coords.top <= 0 && coords.bottom >= 0) {
          link.classList.add("active");
        } else {
          link.classList.remove("active");
        }
      });
    };

    if (document.querySelector(".terms-conditions-toc")) {
      document.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (!blok) return null;

  return (
    <div {...(blok && storyblokEditable(blok))}>
      <div className="terms py-3 py-md-4 py-xl-5" id="service_specific_terms">
        <section className="container">
          <div className="row justify-content-center">
            {/* Table of contents */}
            <div className="col-md-10 col-lg-8 col-xxl-6 mb-5 mb-md-0">
              <ol className="list-unstyled terms-conditions-toc">
                <li className="bg-white rounded-3 p-2 shadow mb-0 terms-conditions-toc-links">
                  <div className="p-2 pt-0">
                    <ol className="py-1">
                      {[
                        { id: "interpretation", text: "Interpretation" },
                        { id: "commencement_and_duration", text: "Commencement and duration" },
                        { id: "authorisation_and_licence", text: "Authorisation and Licence" },
                        { id: "authorised_channel_partner_undertakings", text: "Authorised Channel Partner's undertakings" },
                        { id: "supply_of_services", text: "Supply of Services" },
                        { id: "licensors_undertakings", text: "Licensor's undertakings" },
                        { id: "fees_and_payment", text: "Fees and payment" },
                        { id: "vat_and_taxes", text: "VAT and taxes" },
                        { id: "advertising_and_promotion", text: "Advertising and promotion" },
                        { id: "licensors_services_agreement", text: "Licensor's Services Agreement" },
                        { id: "trade_marks", text: "Trade Marks" },
                        { id: "compliance_with_laws", text: "Compliance with laws and policies" },
                        { id: "data_protection", text: "Data protection" },
                        { id: "non_compete", text: "Non-Compete" },
                        { id: "service_liability_insurance", text: "Service liability insurance and Service recalls" },
                        { id: "indemnities", text: "Indemnities" },
                        { id: "limitation_of_liability", text: "Limitation of liability" },
                        { id: "termination", text: "Termination" },
                        { id: "consequences_of_termination", text: "Consequences of termination" },
                        { id: "confidentiality", text: "Confidentiality" },
                        { id: "force_majeure", text: "Force Majeure" },
                        { id: "entire_agreement", text: "Entire agreement" },
                        { id: "amendment", text: "Amendment" },
                        { id: "assignment", text: "Assignment and other dealings" },
                        { id: "waiver", text: "Waiver" },
                        { id: "severance", text: "Severance" },
                        { id: "notices", text: "Notices" },
                        { id: "third_party_rights", text: "Third party rights" },
                        { id: "no_partnership", text: "No partnership or agency" },
                        { id: "anti_bribery", text: "Anti-bribery and anti-corruption" },
                        { id: "anti_slavery", text: "Anti-slavery and human trafficking" },
                        { id: "counterparts", text: "Counterparts" },
                        { id: "announcements", text: "Announcements" },
                        { id: "conflict", text: "Conflict" },
                        { id: "rights_and_remedies", text: "Rights and remedies" },
                        { id: "set_off", text: "Set-off" },
                        { id: "interest", text: "Interest" },
                        { id: "governing_law", text: "Governing law, venue and dispute resolution" }
                      ].map((item) => (
                        <li key={item.id}>
                          <a className="fw-bold text-primary" href={`#${item.id}`}>{item.text}</a>
                        </li>
                      ))}
                    </ol>
                  </div>
                </li>
              </ol>
            </div>

            {/* Terms Content */}
            <div className="col-md-14 col-lg-16 col-xxl-18 main terms-conditions-toc-content">
              <h2 className="h4">PARTIES</h2>
              <ol>
                <li>Licensor as defined in the Channel Partner Authorisation Form and Agreement.</li>
                <li>Authorised Channel Partner as defined in the Channel Partner Authorisation Form and Agreement.</li>
              </ol>
              <h2 className="h4">BACKGROUND</h2>
              <p>Licensor wishes to authorise and license Authorised Channel Partner as its non-exclusive channel
                partner for the promotion, access to and sale of the Services, and Authorised Channel Partner wishes to
                promote and sell the Services on the terms of the Channel Partner Authorisation Agreement.</p>
              <h2 className="h4">AGREED TERMS</h2>
              <ol className="decimal-list">
                <li id="interpretation">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Interpretation</h3>
                  <p>The following definitions and rules of interpretation apply in the Channel Partner Authorisation
                    Agreement and the Background:</p>
                  <ol className="decimal-list">
                    <li>
                      <h4>Definitions:</h4>
                      <p><strong>Business Day:</strong> a day, other than a Saturday, Sunday or public holiday in
                        England, when banks in London are open for business.</p>
                      <p><strong>Business Hours:</strong> the period from 9.00 am to 5.00 pm GMT/BST on any Business
                        Day.</p>
                      <p><strong>Commencement Date:</strong> has the meaning given in clause 2.</p>
                      <p><strong>Control:</strong> the beneficial ownership of more than 50% of the issued share capital
                        of a company or the legal power to direct or cause the direction of the general management of
                        the company, and <strong>Controls</strong>, <strong>Controlled</strong> and the
                        expression <strong>change of Control </strong>shall be interpreted accordingly.</p>
                      <p><strong>Services:</strong> the Services of the type and specification listed on the Channel
                        Partner Authorisation Form as varied from time to time in accordance with clause 5.2, and any
                        other Services developed by Licensor and which Licensor may license Authorised Channel Partner,
                        by express notice in writing, to offer..</p>
                      <p><strong>Services Agreement:</strong> Licensor&apos;s Services Agreement Standard Terms and
                        Service-specific Terms (as applicable to an Order Form), as amended from time to time.</p>
                      <p><strong>Term:</strong> the term of the Channel Partner Authorisation Agreement, as per the
                        Channel Partner Authorisation Form.</p>
                      <p><strong>Trade Marks:</strong> the registered trade marks and any further trade marks, that
                        Licensor may by express writing permit or procure permission for, Authorised Channel Partner to
                        use in respect of the Services.</p>
                      <p><strong>VAT:</strong> value added tax or any equivalent tax chargeable in the UK or elsewhere.
                      </p>
                    </li>
                    <li>
                      Clause, Schedule and paragraph headings shall not affect the interpretation of the Channel Partner
                      Authorisation Agreement.
                    </li>
                    <li>A <strong>person</strong> includes a natural person, corporate or unincorporated body whether or
                      not having separate legal personality.
                    </li>
                    <li>Any Schedules form part of the Channel Partner Authorisation Agreement and shall have effect as
                      if set out in full in the body of the Channel Partner Authorisation Agreement. Any reference to
                      the Channel Partner Authorisation Agreement includes any Schedules.
                    </li>
                    <li>A reference to a <strong>company</strong> shall include any company, corporation or other body
                      corporate, wherever and however incorporated or established.
                    </li>
                    <li>Unless the context otherwise requires, words in the singular shall include the plural and in the
                      plural shall include the singular.
                    </li>
                    <li>Unless the context otherwise requires, a reference to one gender shall include a reference to
                      the other genders.
                    </li>
                    <li>The Channel Partner Authorisation Agreement shall be binding on, and ensure to the benefit of,
                      the parties to the Channel Partner Authorisation Agreement and their respective personal
                      representatives, successors and permitted assigns, and references to any party shall include that
                      party&apos;s personal representatives, successors and permitted assigns.
                    </li>
                    <li>A reference to legislation or a legislative provision is a reference to it as amended, extended
                      or re-enacted from time to time and shall include all subordinate legislation made under that
                      legislation or legislative provisions.
                    </li>
                    <li>A reference to <strong>writing</strong> or <strong>written</strong> excludes but not email.</li>
                    <li>Any obligation on a party not to do something includes an obligation not to allow that thing to
                      be done.
                    </li>
                    <li>References to clauses and any schedules are to the clauses and schedules of the Channel
                      Partner&nbsp;Authorisation Agreement and references to paragraphs are to paragraphs of the
                      relevant Schedule.
                    </li>
                    <li>Any words following the terms <strong>including, include, in particular, for example</strong> or
                      any similar expression shall be interpreted as illustrative and shall not limit the sense of the
                      words preceding those terms.
                    </li>
                  </ol>
                </li>
                <li id="commencement_and_duration">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Commencement and duration</h3>
                  <p>The Channel Partner Authorisation Agreement shall commence on the Commencement Date set forth in
                    the Channel Partner Authorisation Form and shall continue, unless terminated in accordance with the
                    Channel Partner Authorisation Agreement, for an initial term as set forth in the Channel Partner
                    Authorisation Form and automatically renew for equal extension terms indefinitely after that until
                    terminated by either party giving at least six months&rsquo; express written notice.</p>
                </li>
                <li id="authorisation_and_licence">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Authorisation and Licence</h3>
                  <ol className="decimal-list">
                    <li>As set forth in the Channel Partner Authorisation Form, Licensor authorises and licences
                      Authorised Channel Partner as its non-exclusive channel partner to market, purchase, distribute,
                      access as required and resell the Services on the terms of the Channel Partner Authorisation
                      Agreement, and Authorised Channel Partner accepts the authorisation.
                    </li>
                    <li>Licensor authorises Authorised Channel Partner to execute contracts for sale of the Services
                      without prior reference to Licensor, but only in accord with the Services Agreement. For the
                      avoidance of doubt, Authorised Channel Partner shall not have the authority to amend any of the
                      Services Agreement and the Services Agreement shall take precedence over any other terms.
                    </li>
                    <li>
                      <p>Licensor shall be free to, except as otherwise set forth in the Channel Partner Authorisation
                        Form or expressly agreed in writing:</p>
                      <ol type="a">
                        <li>authorise any other channel partner, reseller or agent and</li>
                        <li>supply any Services directly whether for use or resale.</li>
                      </ol>
                    </li>
                    <li>Authorised Channel Partner shall buy the Services for its own account for resale under the
                      Channel Partner Authorisation Agreement.
                    </li>
                    <li>Authorised Channel Partner shall purchase the Services only from Licensor.</li>
                    <li>Authorised Channel Partner shall not:
                      <ol type="a">
                        <li>represent itself as an agent of Licensor for any purpose;</li>
                        <li>pledge Licensor&apos;s credit;</li>
                        <li>give any condition or warranty on Licensor&apos;s behalf;</li>
                        <li>make any representation on Licensor&apos;s behalf;</li>
                        <li>commit Licensor to any contracts; or</li>
                        <li>otherwise incur any liability for or on behalf of Licensor.</li>
                      </ol>
                    </li>
                    <li>Authorised Channel Partner shall not, without Licensor&apos;s prior written consent, make any
                      promises or guarantees about the Services beyond those contained in the Services Agreement or
                      promotional material supplied by Licensor.
                    </li>
                  </ol>
                </li>
                <li id="authorised_channel_partner_undertakings">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Authorised Channel Partner&apos;s
                    undertakings</h3>
                  <p>Authorised Channel Partner undertakes and agrees with Licensor that at all times during the Term it
                    will:</p>
                  <ol type="a">
                    <li>use reasonable endeavours to promote and sell the Services and to expand the sale of the
                      Services by
                      reasonable and proper means and not do anything which may hinder or interfere with such sales;
                    </li>
                    <li>engage a sufficient number of suitably qualified and trained personnel to ensure the proper
                      fulfilment of Authorised Channel Partner&apos;s obligations under the Channel Partner Authorisation
                      Agreement;
                    </li>
                    <li>ensure attendance of training of Authorised Channel Partner&rsquo;s sales and technical
                      personnel.
                    </li>
                    <li>not resell the Services at a price exceeding any maximum resale price from time to time
                      specified by
                      Licensor in writing;
                    </li>
                    <li>submit written reports at regular intervals to Licensor, showing details of sales, outstanding
                      customer orders and orders placed by Authorised Channel Partner with Licensor that are still
                      outstanding, and any other information relating to the performance of its obligations under the
                      Channel Partner Authorisation Agreement that Licensor may reasonably require from time to time;
                    </li>
                    <li>maintain, on its own account, full access to the Services so that it can supply all orders for
                      Services received by it without delay;
                    </li>
                    <li>keep full and accurate books of account and records clearly showing all enquiries, quotations,
                      transactions and proceedings relating to the Services and allow Licensor, on reasonable notice
                      during
                      normal business hours, access to such accounts and records for inspection;
                    </li>
                    <li>inform Licensor immediately of any change in Control of Authorised Channel Partner, and of any
                      change in its organisation or method of doing business that might be expected to affect the
                      performance of Authorised Channel Partner&apos;s duties under the Channel Partner Authorisation
                      Agreement;
                      and
                    </li>
                    <li>pay or ensure payment on the due date to Licensor of all sums due to Licensor for sales of the
                      Services.
                    </li>
                  </ol>
                </li>
                <li id="supply_of_services">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Supply of Services</h3>
                  <ol className="decimal-list">
                    <li>Licensor shall use its reasonable endeavours to maintain access to and use of the Services and
                      meet
                      all orders for the Services forwarded to it by Authorised Channel Partner as soon as practicable,
                      but
                      Licensor may, at its discretion, refuse any order that it reasonably considers it would be
                      impracticable to attempt to meet.
                    </li>
                    <li>On giving at least three (3) months&rsquo; notice in writing to Authorised Channel Partner,
                      Licensor
                      may vary the Services listed on the Channel Partner Authorisation Form as it deems necessary to
                      exclude one or more of the Services.
                    </li>
                    <li>Licensor may make changes to the specifications of the Services, provided the changes do not
                      adversely affect the quality and nature of the Services. Licensor shall give notice of any changes
                      to
                      Service specifications to Authorised Channel Partner as soon as reasonably practicable.
                    </li>
                  </ol>
                </li>
                <li id="licensors_undertakings">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Licensor&apos;s undertakings</h3>
                  <p>Licensor agrees that at all times during the Term it shall:</p>
                  <ol className="decimal-list">
                    <li>Provide Authorised Channel Partner with such information and support as Licensor, in its sole
                      discretion, considers appropriate to enable Authorised Channel Partner to discharge its duties
                      under
                      the Channel Partner Authorisation Agreement properly and efficiently.
                    </li>
                    <li>Endeavour to respond as soon as practicable to any reasonable enquiries from Authorised Channel
                      Partner concerning the Services.
                    </li>
                    <li>Provide training for Authorised Channel Partner&rsquo;s sales and technical personnel the scope
                      and
                      subject matter of which shall be at Licensor&rsquo;s sole discretion. Licensor shall provide
                      additional training if requested by Authorised Channel Partner at Authorised Channel
                      Partner&rsquo;s
                      expense.
                    </li>
                  </ol>
                </li>
                <li id="fees_and_payment">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Fees and payment</h3>
                  <ol className="decimal-list">
                    <li>Licensor may choose to increase the Fees on an annual basis with effect from each anniversary of
                      the
                      date of any Channel Partner Services Agreement, upon 90 days&rsquo; notice, to cover, e.g., any
                      increased Supplier costs, in line with the higher of five percent (5%) or the percentage increase
                      in
                      the Consumer Price Index in the preceding 12-month period, and the first such increase shall take
                      effect, at Licensor&rsquo;s discretion, on the first anniversary of the date of any Channel
                      Partner
                      Services Agreement and shall be based on the latest available annual figure for the percentage
                      increase in the Consumer Prices Index. Licensor may, at any time during the Initial Term and
                      during
                      any Extension Term thereafter, vary the Fees payable by Customer by giving at least 90 days prior
                      written notice in the event of and where related to new taxation laws, or the introduction or
                      increase
                      in any taxes, levies, costs or material expenses, including any taxes or levies which relate to
                      the
                      Services;
                    </li>
                    <li>Licensor may provide Authorised Channel Partner with discounts as expressly agreed in writing.
                    </li>
                    <li>
                      <p>Authorised Channel Partner shall collect and hold as trustee all monies due to Licensor for any
                        sales or otherwise and shall remit the same to Licensor without deduction and shall pay
                        invoices,
                        except where otherwise expressly agreed:</p>
                      <ol type="a">
                        <li>within thirty (30) days of the receipt of the invoices; and</li>
                        <li>in full and in cleared funds to the bank account nominated in writing by Licensor.</li>
                      </ol>
                    </li>
                    <li>All expenses, costs and charges incurred by Authorised Channel Partner in the performance of its
                      obligations under the Channel Partner Authorisation Agreement shall be paid by Authorised Channel
                      Partner, unless Licensor has expressly agreed in advance in writing to pay such expenses, costs
                      and
                      charges.
                    </li>
                    <li>As between Licensor and Authorised Channel Partner, Authorised Channel Partner is solely
                      responsible
                      for the collection, remittance and payments of any or all taxes, charges, levies, assessments and
                      other fees of any kind imposed by governmental or other authority in respect of the purchase,
                      sale,
                      importation, lease or other distribution of the Services.
                    </li>
                  </ol>
                </li>
                <li id="vat_and_taxes">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">VAT and taxes</h3>
                  <ol className="decimal-list">
                    <li>All sums payable under the Channel Partner Authorisation Agreement, or otherwise payable by any
                      party to any other party under the Channel Partner Authorisation Agreement are exclusive of any
                      VAT
                      chargeable on the supplies for which such sums (or any part of them) are the whole or part of the
                      consideration for VAT purposes.
                    </li>
                    <li>Where, under the Channel Partner Authorisation Agreement, any party makes a supply to any other
                      party (Recipient) for VAT purposes and VAT is or becomes chargeable on that supply for which the
                      supplying party is required to account to the relevant tax authority, the Recipient shall, subject
                      to
                      the receipt of a valid VAT invoice, pay the supplying party (in addition to, and at the same time
                      as,
                      any other consideration for that supply) the amount of such VAT.
                    </li>
                    <li>Where any party is required by the Channel Partner Authorisation Agreement to reimburse or
                      indemnify
                      any other party for any cost or expense, that first party shall reimburse or indemnify the other
                      party
                      for the full amount of the cost or expense, including any VAT on that amount, except to the extent
                      that the other party is entitled to credit or repayment for that VAT from any relevant tax
                      authority.
                    </li>
                    <li>All taxes, charges, levies, assessments and other fees of any kind imposed on the purchase or
                      import
                      of the Services shall be the responsibility of, and for the account of, Authorised Channel
                      Partner.
                    </li>
                  </ol>
                </li>
                <li id="advertising_and_promotion">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Advertising and promotion</h3>
                  <ol className="decimal-list">
                    <li>
                      <p>Authorised Channel Partner shall:</p>
                      <ol type="a">
                        <li>not use any advertising materials or promotional literature to promote the Services without
                          Licensor&apos;s express written consent;
                        </li>
                        <li>display Licensor advertising as reasonably requested Licensor;</li>
                        <li>observe all reasonable directions and instructions given to it by Licensor for promotion and
                          advertisement of the Services; and
                        </li>
                        <li>not make any written statement as to the quality of the Services without the prior written
                          approval of Licensor.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Licensor shall:</p>
                      <ol type="a">
                        <li>approve or reject, in its sole discretion, any promotional information or material or any
                          advertising and promotional programme submitted by Authorised Channel Partner; and
                        </li>
                        <li>provide Authorised Channel Partner with information on the advertising and promotion used by
                          Licensor and, at Licensors&rsquo; discretion, charge Authorised Channel Partner for
                          Licensor&rsquo;s supply of promotional and advertising material as Authorised Channel Partner
                          shall reasonably request from time to time.
                        </li>
                      </ol>
                    </li>
                    <li>Authorised Channel Partner shall ensure that any website that it uses for the sale of the
                      Services
                      complies with any reasonable quality standards and criteria provided by Licensor from time to
                      time.
                    </li>
                  </ol>
                </li>
                <li id="licensors_services_agreement">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Licensor&apos;s Services Agreement</h3>
                  <ol className="decimal-list">
                    <li>The Services Agreement shall apply to the Services in respect of the Channel Partner
                      Authorisation
                      Agreement and shall be read in conjunction with the Channel Partner Authorisation Agreement. If
                      there
                      is any inconsistency between the Services Agreement and the provisions of the Channel Partner
                      Authorisation Agreement, the provisions of the Services Agreement shall prevail as relates to
                      access
                      to and delivery of the Services.
                    </li>
                    <li>Authorised Channel Partner shall not, without Licensor&rsquo;s prior written consent, make or
                      give
                      any representations, warranties or other promises concerning the Services beyond those contained
                      in
                      the Services Agreement.
                    </li>
                  </ol>
                </li>
                <li id="trade_marks">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Trade Marks</h3>
                  <ol className="decimal-list">
                    <li>Licensor grants Authorised Channel Partner the non-exclusive right to use the Trade Marks in
                      the
                      promotion, advertisement and sale of the Services, subject to, and for the duration of, the
                      Channel
                      Partner Authorisation Agreement. Authorised Channel Partner acknowledges and agrees that all
                      rights
                      in the Trade Marks shall remain in Licensor, and that Authorised Channel Partner has and will
                      acquire no right in them by virtue of the discharge of its obligations under the Channel Partner
                      Authorisation Agreement, except for the right to use the Trade Marks as expressly provided in
                      the
                      Channel Partner Authorisation Agreement.
                    </li>
                    <li>Authorised Channel Partner shall market and sell the Services only under the Trade Marks, and
                      not
                      in association with any other trade mark, brand or trade name.
                    </li>
                    <li>
                      <p>Authorised Channel Partner shall not, without the prior written consent of Licensor:</p>
                      <ol type="a">
                        <li>make any addition or modifications to the Services or to any advertising and promotional
                          materials supplied by Licensor; or
                        </li>
                        <li>alter, deface or remove any reference to the Trade Marks, any reference to Licensor or any
                          other name attached or affixed to the Services.
                        </li>
                      </ol>
                    </li>
                    <li>Authorised Channel Partner shall not sub-license, assign, transfer, charge, or otherwise
                      encumber
                      the right to use, reference, or designate the Trade Marks to any other party, except as
                      otherwise
                      expressly permitted under the Channel Partner Authorisation Agreement.
                    </li>
                    <li>Authorised Channel Partner shall not do, or omit to do, anything in its use of the Trade Marks
                      that could adversely affect their validity or reputation.
                    </li>
                    <li>
                      <p>Authorised Channel Partner shall promptly give notice to Licensor in writing if it becomes
                        aware
                        of:</p>
                      <ol type="a">
                        <li>any infringement or suspected infringement of the Trade Marks or any other intellectual
                          property rights relating to the Services; or
                        </li>
                        <li>any claim that any Service or the manufacture, use, sale or other disposal of any Service,
                          whether or not under the Trade Marks, infringes the rights of any third party.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>In respect of any matter that falls within clause 11.6(a):</p>
                      <ol type="a">
                        <li>Licensor shall in its absolute discretion, decide what action to take in respect of the
                          matter
                          (if any);
                        </li>
                        <li>Licensor shall conduct and have sole control over any consequent action that it deems
                          necessary and Authorised Channel Partner shall on being so requested by Licensor and at
                          Licensor&apos;s cost assist in taking all steps to defend the rights of Licensor including the
                          institution at Licensor&apos;s cost of any actions which it may deem necessary to commence for
                          the
                          protection of any of its rights; and
                        </li>
                        <li>Licensor shall pay all costs in relation to that action and shall be entitled to all
                          damages
                          and other sums that may be paid or awarded as a result of that action.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>In respect of any matter that falls within clause 11.6(b):</p>
                      <ol type="a">
                        <li>
                          <p>Licensor and Authorised Channel Partner shall agree:</p>
                          <ol>
                            <li>what steps to take to prevent or terminate the infringement; and</li>
                            <li>the proportions in which they shall share the cost of those steps and any damages and
                              other sums that may be awarded to or against them; and
                            </li>
                          </ol>
                        </li>
                        <li>failing agreement between the parties, either party may take any action as it considers
                          necessary or appropriate, at its own expense, to defend the claim and shall be entitled to
                          and
                          responsible for all damages and other sums that may be recovered or awarded against it as a
                          result of that action.
                        </li>
                      </ol>
                    </li>
                    <li>Authorised Channel Partner shall not use the Trade Marks as part of the name under which
                      Authorised Channel Partner conducts its business, or any connected business, or under which it
                      sells
                      or services any Services (except the Services), or in any other way, except as expressly
                      permitted
                      under the Channel Partner Authorisation Agreement.
                    </li>
                    <li>Upon termination of the Channel Partner Authorisation Agreement for any reason, Authorised
                      Channel
                      Partner shall immediately stop using all or any part of the Trade Marks;
                    </li>
                    <li>Licensor may upon its written notice use Authorised Channel Partner&rsquo;s logo(s) and/or
                      trade
                      marks for promotion of Licensor&rsquo;s Channel Partner scheme and other related marketing,
                      unless
                      otherwise required by Authorised Channel Partner.
                    </li>
                  </ol>
                </li>
                <li id="compliance_with_laws">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Compliance with laws and policies</h3>
                  <p>Authorised Channel Partner shall at its own expense comply with all laws and regulations relating
                    to
                    its activities under the Channel Partner Authorisation Agreement, as they may change from time to
                    time, and with any conditions binding on it in any applicable licences, registrations, permits and
                    approvals.</p>
                </li>
                <li id="data_protection">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Data protection (<em>except where the Services
                    Agreement Standard Terms are applicable as to Services and thereby supersede these terms</em>)
                  </h3>
                  <ol className="decimal-list">
                    <li>
                      <p>The following definitions apply in this clause:</p>
                      <p><strong>Controller, processor, data subject, personal data, personal data breach, processing
                        and
                        appropriate technical and organisation measures:</strong> as set out in the Data Protection
                        Legislation.</p>
                      <p><strong>Data Protection Legislation:</strong> all applicable data protection and privacy
                        legislation in force from time to time in the UK including the UK GDPR; the Data Protection
                        Act
                        2018 (DPA 2018) (and regulations made thereunder); the Privacy and Electronic Communications
                        Regulations 2003 (<em>SI 2003/2426</em>) as amended and all other legislation and regulatory
                        requirements in force from time to time which apply to a party relating to the use of personal
                        data (including, as applicable, the privacy of electronic communications and European
                        Regulation
                        (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016, on the
                        protection of
                        natural persons with regard to the processing of personal data and on the free movement of
                        such
                        data, and repealing Directive 95/46/EC (General Data Protection Regulation)).</p>
                      <p><strong>Permitted Recipients:</strong> the parties to the Channel Partner Authorisation
                        Agreement, the employees of each party, any third parties engaged to perform obligations in
                        connection with the Channel Partner Authorisation Agreement.</p>
                      <p><strong>Shared Personal Data:</strong> the personal data to be shared between the parties
                        under
                        the Channel Partner Authorisation Agreement. Shared Personal Data shall be confined to the
                        categories of information relevant to the relevant categories of data subject.</p>
                      <p><strong>UK GDPR:</strong> has the meaning given to it in section 3(10) (as supplemented by
                        section 205(4)) of the DPA 2018.</p>
                    </li>
                    <li>This clause 13 sets out the framework for the sharing of personal data between the parties as
                      controllers. Each party acknowledges that one party (the Data Discloser) will regularly disclose
                      to
                      the other party Shared Personal Data collected by the Data Discloser for the Agreed Purposes.
                    </li>
                    <li>Each party shall comply with all the obligations imposed on a controller under the Data
                      Protection
                      Legislation, and any material breach of the Data Protection Legislation by one party shall
                      constitute a material breach.
                    </li>
                    <li>
                      <p>Each party shall:</p>
                      <ol type="a">
                        <li>ensure that it has all necessary notices and consents and lawful bases in place to enable
                          lawful transfer of the Shared Personal Data to the Permitted Recipients for the Agreed
                          Purposes;
                        </li>
                        <li>give full information to any data subject whose personal data may be processed under the
                          Channel Partner Authorisation Agreement of the nature of such processing. This includes
                          giving
                          notice that, on the termination or expiry of the Channel Partner Authorisation Agreement,
                          personal data relating to them may be retained by or, as the case may be, transferred to one
                          or
                          more of the Permitted Recipients, their successors and assignees;
                        </li>
                        <li>process the Shared Personal Data only for the Agreed Purposes;</li>
                        <li>not disclose or allow access to the Shared Personal Data to anyone other than the
                          Permitted
                          Recipients;
                        </li>
                        <li>ensure that all Permitted Recipients are subject to written contractual obligations
                          concerning
                          the Shared Personal Data including obligations of confidentiality which are no less
                          demanding
                          than those imposed by the Channel Partner Authorisation Agreement;
                        </li>
                        <li>ensure that it has in place appropriate technical and organisational measures, reviewed
                          and
                          approved by the other party, to protect against unauthorised or unlawful processing of
                          personal
                          data and against accidental loss or destruction of, or damage to, personal data; and
                        </li>
                        <li>
                          <p>not transfer any personal data received from the Data Discloser outside the UK unless the
                            transferor ensures that:</p>
                          <ol>
                            <li>the transfer is to a country approved under the applicable Data Protection Legislation
                              as
                              providing adequate protection; or
                            </li>
                            <li>there are appropriate safeguards or binding corporate rules in place pursuant to the
                              applicable Data Protection Legislation; or
                            </li>
                            <li>the transferor otherwise complies with its obligations under the applicable Data
                              Protection Legislation by providing an adequate level of protection to any personal data
                              that is transferred; or
                            </li>
                            <li>one of the derogations for specific situations in the applicable Data Protection
                              Legislation applies to the transfer.
                            </li>
                          </ol>
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Each party shall assist the other in complying with all applicable requirements of the Data
                        Protection Legislation. In particular, each party shall:</p>
                      <ol type="a">
                        <li>consult with the other party about any notices given to data subjects in relation to the
                          Shared Personal Data;
                        </li>
                        <li>promptly inform the other party about the receipt of any data subject rights request;</li>
                        <li>provide the other party with reasonable assistance in complying with any data subject
                          access
                          request;
                        </li>
                        <li>not disclose, release, amend, delete or block any Shared Personal Data in response to a
                          data
                          subject rights request without first consulting the other party wherever possible;
                        </li>
                        <li>assist the other party, at the cost of the other party, in responding to any request from
                          a
                          data subject and in ensuring compliance with its obligations under the Data Protection
                          Legislation with respect to security, personal data breach notifications, data protection
                          impact
                          assessments and consultations with the Information Commissioner or other regulators;
                        </li>
                        <li>notify the other party without undue delay on becoming aware of any breach of the Data
                          Protection Legislation;
                        </li>
                        <li>at the written direction of the Data Discloser, delete or return Shared Personal Data and
                          copies thereof to the Data Discloser on termination or expiry of the Channel Partner
                          Authorisation Agreement unless required by law to store the Shared Personal Data;
                        </li>
                        <li>use compatible technology for the processing of Shared Personal Data to ensure that there
                          is
                          no lack of accuracy resulting from personal data transfers;
                        </li>
                        <li>maintain complete and accurate records and information to demonstrate its compliance with
                          this
                          clause 13; and
                        </li>
                        <li>provide the other party with contact details of at least one employee as point of contact
                          and
                          responsible manager for all issues arising out of the Data Protection Legislation, including
                          the
                          joint training of relevant staff, the procedures to be followed in the event of a data
                          security
                          breach, and the regular review of the parties&apos; compliance with the Data Protection
                          Legislation.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Each party shall indemnify the other against all liabilities, costs, expenses, damages and
                        losses
                        (including any direct, indirect or consequential losses, loss of profit, loss of reputation
                        and
                        all interest, penalties and legal costs (calculated on a full indemnity basis) and all other
                        reasonable professional costs and expenses) suffered or incurred by the indemnified party
                        arising
                        out of or in connection with the breach of the Data Protection Legislation by the indemnifying
                        party, its employees or agents, provided that the indemnified party gives to the indemnifier
                        prompt notice of such claim, full information about the circumstances giving rise to it,
                        reasonable assistance in dealing with the claim and sole authority to manage, defend and/or
                        settle
                        it.</p>
                    </li>
                  </ol>
                </li>
                <li id="non_compete">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Non-Compete</h3>
                  <p>During the term of the Channel Partner Authorisation Agreement, and for a period of one (1) year
                    immediately following its termination, Authorised Channel Partner agrees not to directly or
                    indirectly
                    solicit, enter into a contractual arrangement with, or perform competing Services or provide
                    competing
                    deliverables&nbsp; or assist any third party to do the same.</p>
                </li>
                <li id="service_liability_insurance">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Service liability insurance and Service
                    recalls</h3>
                  <ol className="decimal-list">
                    <li>During the Term, Licensor shall maintain Service liability insurance with a reputable insurer
                      of
                      no less than three million pounds (&pound;3,000,000) in total for any and all liability (however
                      arising) for a claim that the Services are faulty or defective. Licensor shall provide a copy of
                      the
                      insurance policy to Authorised Channel Partner on request.
                    </li>
                    <li>Authorised Channel Partner undertakes to maintain appropriate, up-to-date and accurate records
                      to
                      enable the immediate recall or withdrawal, where possible and necessary, of any Services or
                      batches
                      of Services. These records shall include records of deliveries to/access by customers (including
                      batch numbers, delivery date(s), name and address of customer, telephone number and email
                      address.
                    </li>
                    <li>Authorised Channel Partner shall, at Licensor&apos;s cost, give any assistance that Licensor shall
                      reasonably require to recall, as a matter of urgency, any Services.
                    </li>
                  </ol>
                </li>
                <li id="indemnities">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Indemnities</h3>
                  <ol className="decimal-list">
                    <li>
                      <p>Subject to Authorised Channel Partner fulfilling all the conditions in this clause, Licensor
                        shall indemnify Authorised Channel Partner against all liabilities, costs, expenses, damages
                        and
                        losses including any direct or indirect losses and all interest, penalties and legal costs and
                        all
                        other professional costs and expenses suffered or incurred by Authorised Channel Partner
                        arising
                        out of or in connection with:</p>
                      <ol type="a">
                        <li>any claim made against Authorised Channel Partner in respect of damage to property, death
                          or
                          personal injury arising from any fault or defect in the Services; and
                        </li>
                        <li>any claim made against Authorised Channel Partner for actual or alleged infringement of a
                          third party&apos;s intellectual property rights arising out of or in connection with the sale of
                          the
                          Services by Authorised Channel Partner.
                        </li>
                      </ol>
                    </li>
                    <li>The indemnities in clause 16.1 shall not cover Authorised Channel Partner to the extent that
                      the
                      claim results from Authorised Channel Partner&apos;s negligence or wilful misconduct.
                    </li>
                    <li>
                      <p>Liability under the indemnities in clause 1 6.1 is conditional on Authorised Channel Partner
                        discharging the following obligations. If any third party makes a claim, or notifies an
                        intention
                        to make a claim, against Authorised Channel Partner which may reasonably be considered likely
                        to
                        give rise to a liability under clause 16.1 (<strong>Claim</strong>), Authorised Channel
                        Partner
                        shall:</p>
                      <ol type="a">
                        <li>as soon as reasonably practicable, give written notice of the Claim to Licensor,
                          specifying
                          the nature of the Claim in reasonable detail;
                        </li>
                        <li>not make any admission of liability, agreement or compromise in relation to the Claim
                          without
                          the prior written consent of Licensor;
                        </li>
                        <li>give Licensor and its professional advisers access at reasonable times (on reasonable
                          prior
                          notice) to its premises and its officers, directors, employees, agents, representatives or
                          advisers, and to any relevant assets, accounts, documents and records within the power or
                          control of Authorised Channel Partner, so as to enable Licensor and its professional
                          advisers to
                          examine them and to take copies (at Licensor&apos;s expense) for the purpose of assessing the
                          Claim;
                          and
                        </li>
                        <li>take such action as Licensor may reasonably request to avoid, dispute, compromise or
                          defend
                          the Claim.
                        </li>
                      </ol>
                    </li>
                    <li>If a payment due from Licensor under this clause is subject to tax (whether by way of direct
                      assessment or withholding at its source), Authorised Channel Partner shall be entitled to
                      receive
                      from Licensor such amounts as shall ensure that the net receipt, after tax, to Authorised
                      Channel
                      Partner in respect of the payment is the same as it would have been were the payment not subject
                      to
                      tax.
                    </li>
                    <li>Nothing in this clause shall restrict or limit Authorised Channel Partner&apos;s general obligation
                      at
                      law to mitigate a loss it may suffer or incur as a result of an event that may give rise to a
                      claim
                      under this indemnity.
                    </li>
                  </ol>
                </li>
                <li id="limitation_of_liability">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Limitation of liability</h3>
                  <ol className="decimal-list">
                    <li>Each party has obtained insurance cover in respect of its own legal liability for individual
                      claims not exceeding &pound;5 million per claim and in the aggregate. The limits and exclusions
                      in
                      this clause reflect the insurance cover each party has been able to arrange and each party is
                      responsible for making its own arrangements for the insurance of any excess loss.
                    </li>
                    <li>The restrictions on liability in this clause apply to every liability arising under or in
                      connection with the Channel Partner Authorisation Agreement including liability in contract,
                      tort
                      (including negligence), misrepresentation, restitution or otherwise.
                    </li>
                    <li>
                      <p>Specific heads of excluded loss.&nbsp;Subject to no limitation of Authorised Channel
                        Partner&rsquo;s payment obligations, clause 17.4 (liabilities which cannot legally be
                        limited),
                        this clause 12.5 specifies the types of losses that are excluded:</p>
                      <ol type="a">
                        <li>Loss of profits;</li>
                        <li>Loss of revenues, loss of goodwill;</li>
                        <li>Loss of agreements, loss of business opportunity;</li>
                        <li>Loss of business;</li>
                        <li>Depletion of goodwill or similar losses;</li>
                        <li>Pure economic loss; and</li>
                        <li>For any indirect or consequential loss, costs, damages, charges or expenses however
                          arising
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Nothing in the agreement limits any liability which cannot legally be limited, including
                        liability for:</p>
                      <ol type="a">
                        <li>death or personal injury caused by negligence;</li>
                        <li>fraud or fraudulent misrepresentation; and</li>
                      </ol>
                    </li>
                    <li>Terms implied by sections 13 to 15 of the Sale of Goods Act 1979 and sections 3, 4 and 5 of
                      the
                      Supply of Goods and Services Act 1982 are, to the fullest extent permitted by law, excluded from
                      the
                      Channel Partner Authorisation Agreement.
                    </li>
                  </ol>
                </li>
                <li id="termination">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Termination</h3>
                  <ol className="decimal-list">
                    <li>
                      <p>Either party may immediately terminate the Channel Partner Authorisation Agreement without
                        payment of compensation or other damages caused to the other solely by such termination by
                        giving
                        notice to the other if any one or more of the following occurs:</p>
                      <ol type="a">
                        <li>the other party commits a material breach of any term of the Channel Partner Authorisation
                          Agreement and such breach is irremediable or (if such breach is remediable) fails to remedy
                          that
                          breach within 30 days after being notified in writing to do so;
                        </li>
                        <li>the other party suspends, or threatens to suspend, payment of its debts or is unable to
                          pay
                          its debts as they fall due or admits inability to pay its debts or (being a company or
                          limited
                          liability partnership) is deemed unable to pay its debts within the meaning of section 123
                          of
                          the Insolvency Act 1986, or (being an individual) is deemed either unable to pay its debts
                          or as
                          having no reasonable prospect of so doing, in either case, within the meaning of section 268
                          of
                          the Insolvency Act 1986, or (if a partnership) has any partner to whom any of the foregoing
                          apply;
                        </li>
                        <li>the other party commences negotiations with all or any class of its creditors with a view
                          to
                          rescheduling any of its debts, or makes a proposal for or enters into any compromise or
                          arrangement with any of its creditors other than for the sole purpose of a scheme for a
                          solvent
                          amalgamation of that other party with one or more other companies or the solvent
                          reconstruction
                          of that other party;
                        </li>
                        <li>a petition is filed, a notice is given, a resolution is passed, or an order is made, for
                          or in
                          connection with the winding up of that other party (being a company) other than for the sole
                          purpose of a scheme for a solvent amalgamation of that other party with one or more other
                          companies or the solvent reconstruction of that other party;
                        </li>
                        <li>an application is made to court, or an order is made, for the appointment of an
                          administrator,
                          or if a notice of intention to appoint an administrator is given or if an administrator is
                          appointed, over the other party (being a company);
                        </li>
                        <li>the holder of a qualifying floating charge over the assets of that other party (being a
                          company) has become entitled to appoint or has appointed an administrative receiver;
                        </li>
                        <li>a person becomes entitled to appoint a receiver over all or any of the assets of the other
                          party or a receiver is appointed over all or any of the assets of the other party;
                        </li>
                        <li>a creditor or encumbrancer of the other party attaches or takes possession of, or a
                          distress,
                          execution, sequestration or other such process is levied or enforced on or sued against, the
                          whole or any part of the other party&apos;s assets and such attachment or process is not
                          discharged
                          within 14 days;
                        </li>
                        <li>any event occurs, or proceeding is taken, with respect to the other party in any
                          jurisdiction
                          to which it is subject that has equivalent or similar effect to any event mentioned in
                          clause
                          18.1(b) to clause 18.1(h) (inclusive); or
                        </li>
                        <li>the other party suspends or ceases, or threatens to suspend or cease, carrying on all or a
                          substantial part of its business.
                        </li>
                      </ol>
                    </li>
                    <li>For the purposes of clause 18.1(a) material breach means a breach (including an anticipatory
                      breach) that is serious in the widest sense of having a serious effect on the benefit which the
                      terminating party would otherwise derive from a substantial portion of the Channel Partner
                      Authorisation Agreement.
                    </li>
                    <li>Without affecting any other right or remedy available to it, including payment by Authorised
                      Channel Partner of all Fees due under the Channel Partner Authorisation Agreement, Licensor may
                      terminate the Channel Partner Authorisation Agreement with immediate effect by giving written
                      notice
                      to Authorised Channel Partner if Authorised Channel Partner is in material breach of any other
                      agreement with Licensor or fails to pay any amount due under any Channel Partner Authorisation
                      Agreement on the due date (including, without limitation, all Fees for any delivered services)
                      and
                      remains in default more than 30 days after being notified to make such payment.
                    </li>
                    <li>Authorised Channel Partner may send express written notice of its intention to terminate the
                      Channel Partner Authorisation Agreement within 30 days of the date it receives 90 days express
                      written notice from Licensor of any material update to the Standard Terms (under clause 23)
                      where
                      that updated material term may not by law or policy, applicable at the time, be accepted by
                      Authorised Channel Partner.
                    </li>
                  </ol>
                </li>
                <li id="consequences_of_termination">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Consequences of termination</h3>
                  <ol className="decimal-list">
                    <li>Any provision of the Channel Partner Authorisation Agreement that expressly or by implication
                      is
                      intended to come into or continue in force on or after termination of the Channel Partner
                      Authorisation Agreement shall remain in full force and effect.
                    </li>
                    <li>Termination of the Channel Partner Authorisation Agreement shall not affect any rights,
                      remedies,
                      obligations or liabilities of the parties that have accrued up to the date of termination
                      including
                      the right to claim damages in respect of any breach of the agreement which existed at or before
                      the
                      date of termination.
                    </li>
                    <li>On termination of the Channel Partner Authorisation Agreement Authorised Channel Partner shall
                      immediately pay to Licensor all Licensor&apos;s outstanding unpaid invoices and interest;
                    </li>
                    <li>The termination of the Channel Partner Authorisation Agreement shall not of itself make
                      Licensor
                      liable to pay any compensation to Authorised Channel Partner, including compensation for loss of
                      profits or goodwill.
                    </li>
                    <li>Subject to clause 19.3, all other rights and licences of Authorised Channel Partner under the
                      Channel Partner Authorisation Agreement shall terminate on the date of termination or expiry of
                      the
                      Channel Partner Authorisation Agreement.
                    </li>
                  </ol>
                </li>
                <li id="confidentiality">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Confidentiality</h3>
                  <ol className="decimal-list">
                    <li>Each party undertakes that it shall not disclose to any person any confidential information
                      concerning the business, assets, affairs, customers, clients or suppliers of the other party or
                      of
                      any member of the group of companies to which the other party belongs, except as permitted by
                      clause
                      20.2.
                    </li>
                    <li>
                      <p>Each party may disclose the other party&apos;s confidential information:</p>
                      <ol type="a">
                        <li>to its employees, officers, representatives, contractors, subcontractors or advisers who
                          need
                          to know such information for the purposes of exercising the party&apos;s rights or carrying out
                          its
                          obligations under or in connection with the Channel Partner Authorisation Agreement. Each
                          party
                          shall ensure that its employees, officers, representatives, contractors, subcontractors or
                          advisers to whom it discloses the other party&apos;s confidential information comply with this
                          clause
                          20; and
                        </li>
                        <li>as may be required by law, a court of competent jurisdiction or any governmental or
                          regulatory
                          authority.
                        </li>
                      </ol>
                    </li>
                    <li>No party shall use any other party&apos;s confidential information for any purpose other than to
                      exercise its rights and perform its obligations under or in connection with the Channel Partner
                      Authorisation Agreement.
                    </li>
                  </ol>
                </li>
                <li id="force_majeure">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Force Majeure</h3>
                  <p>Neither party shall be in breach of the Channel Partner Authorisation Agreement or otherwise
                    liable
                    for any failure or delay in the performance of its obligations, except for Authorised Channel
                    Partner&rsquo;s payment obligations, if such delay or failure results from events, circumstances
                    or
                    causes, without limitation, beyond its reasonable control providing, as soon as reasonably
                    practicable
                    after the start of the Force Majeure event but no later than ten days from its start, it notifies
                    the
                    other party of the event, the date on which it started, its likely or potential duration, and the
                    effect of the event on its ability to perform any of its obligations under the Channel Partner
                    Authorisation Agreement; and providing it uses all reasonable endeavours to mitigate the effect of
                    the
                    event on the performance of its obligations. If a period of delay or non-performance continues for
                    six
                    (6) weeks, the party not affected may terminate the Channel Partner Authorisation Agreement by
                    giving
                    twenty-one (21) days&apos; written notice to the affected party.</p>
                </li>
                <li id="entire_agreement">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Entire agreement</h3>
                  <ol className="decimal-list">
                    <li>The Channel Partner Authorisation Agreement constitutes the entire agreement between the
                      parties
                      and supersedes and extinguishes all previous agreements, contracts, promises, assurances,
                      warranties, representations and understandings between them, whether written, oral or by
                      conduct,
                      relating to its subject matter. Each party acknowledges that in entering into the Channel
                      Partner
                      Authorisation Agreement it does not rely on any statement, representation, assurance or warranty
                      (whether made innocently or negligently) that is not set out in the Channel Partner
                      Authorisation
                      Agreement. Each party agrees that it shall have no claim for innocent or negligent
                      misrepresentation
                      or negligent misstatement based on any statement in the Channel Partner Authorisation Agreement.
                    </li>
                  </ol>
                </li>
                <li id="amendment">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Amendment</h3>
                  <p>No amendment or variation of the Channel Partner Authorisation Agreement shall be effective
                    without
                    express written consent signed by the parties (or their authorised representatives) except that
                    Licensor may from time to time update these Channel Partner Authorisation Terms, the Services
                    Agreement Standard Terms or Services Agreement Service-specific Terms upon 90 days express written
                    notice to Authorised Channel Partner upon which Authorised Channel Partner may send express
                    written
                    notice of its intent to terminate the Channel Partner Authorisation Agreement as provided for in
                    clause 18.4.</p>
                </li>
                <li id="assignment">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Assignment and other dealings</h3>
                  <p>Authorised Channel Partner shall not assign, transfer, mortgage, charge, subcontract, delegate,
                    declare a trust over or deal in any other manner with any of its rights and obligations under the
                    Channel Partner Authorisation Agreement, without prior express written consent from Licensor, such
                    consent not to be unreasonably withheld. Licensor may mortgage, charge, delegate, assign, novate
                    or
                    otherwise transfer any or all its rights under the Channel Partner Authorisation Agreement.
                    Licensor
                    shall not novate or assign its rights and obligations under the Channel Partner Authorisation
                    Agreement to another service party without prior written notice to Authorised Channel Partner.</p>
                </li>
                <li id="waiver">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Waiver</h3>
                  <p>A waiver of any right or remedy is only effective if given in writing and shall not be deemed a
                    waiver of any subsequent right or remedy. A delay or failure to exercise, or the single or partial
                    exercise of, any right or remedy shall not waive that or any other right or remedy, nor shall it
                    prevent or restrict the further exercise of that or any other right or remedy.</p>
                </li>
                <li id="severance">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Severance</h3>
                  <p>If any provision or part-provision of the Channel Partner Authorisation Agreement is or becomes
                    invalid, illegal or unenforceable, it shall be deemed deleted, but that shall not affect the
                    validity
                    and enforceability of the rest of the Channel Partner Authorisation Agreement. If any provision or
                    part-provision of the Channel Partner Authorisation Agreement is deemed deleted under clause 0 the
                    parties shall negotiate in good faith to agree a replacement provision that, to the greatest
                    extent
                    possible, achieves the intended commercial result of the original provision.</p>
                </li>
                <li id="notices">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Notices</h3>
                  <ol className="decimal-list">
                    <li>
                      <p>Any notice given to a party under or in connection with the Channel Partner Authorisation
                        Agreement shall be in writing and shall be delivered by hand or by next working day courier or
                        signed-for post at its registered office. Any notice shall be deemed to have been
                        received:</p>
                      <ol type="a">
                        <li>if delivered by hand, at the time the notice is left at the proper address;</li>
                        <li>if sent by courier or signed-for post, when received.</li>
                        <li>if sent by email, at the time of transmission, or, if this time falls outside Business
                          Hours
                          in the place of receipt, when Business Hours resume.
                        </li>
                      </ol>
                    </li>
                    <li>This clause does not apply to the service of any proceedings or other documents in any legal
                      action or, where applicable, any arbitration or other method of dispute resolution.
                    </li>
                  </ol>
                </li>
                <li id="third_party_rights">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Third party rights</h3>
                  <p>Except as otherwise agreed, the Channel Partner Authorisation Agreement does not give rise to any
                    third-party statutory rights to enforce any of its terms.</p>
                </li>
                <li id="no_partnership">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">No partnership or agency</h3>
                  <p>Nothing in the Channel Partner Authorisation Agreement is intended to, or shall be deemed to,
                    establish any partnership or joint venture between the parties, constitute either party the agent
                    of
                    the other party, or authorise either party to make or enter into any commitments for or on behalf
                    of
                    the other party. Each party confirms it is acting on its own behalf and not for the benefit of any
                    other person.</p>
                </li>
                <li id="anti_bribery">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Anti-bribery and anti-corruption</h3>
                  <p>Each Party shall, and shall ensure any of its agents, employees, consultants, contractors and
                    subcontractors shall, comply with all applicable laws, statutes, regulation, and codes relating to
                    anti-bribery and anti-corruption including but not limited to the Bribery Act 2010 and shall
                    establish, maintain and enforce its own policies and procedures to ensure compliance.</p>
                </li>
                <li id="anti_slavery">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Anti-slavery and human trafficking</h3>
                  <p>Each Party shall, in performing its obligations under the Channel Partner Authorisation
                    Agreement,
                    comply with all applicable anti-slavery and human trafficking laws, statutes and regulations from
                    time
                    to time in force including the Modern Slavery Act 2015; and each party represents and warrants
                    that it
                    has not been convicted of any offence involving slavery and human trafficking or been the subject
                    of
                    any investigation, inquiry or enforcement proceedings regarding any offence or alleged offence of
                    or
                    in connection with such trafficking.</p>
                </li>
                <li id="counterparts">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Counterparts</h3>
                  <p>The Channel Partner Authorisation Agreement, specifically the Channel Partner Authorisation Form,
                    may
                    be executed in any number of electronically delivered or hardcopy counterparts, each of which
                    shall
                    constitute a duplicate original, but all the counterparts shall together constitute the one
                    agreement.</p>
                </li>
                <li id="announcements">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Announcements</h3>
                  <p>No party shall make, or permit any person to make, any public announcement concerning the
                    existence,
                    subject matter or terms of the Channel Partner Authorisation Agreement, the wider transactions
                    contemplated by it, or the relationship between the parties, without the prior written consent of
                    the
                    other parties (such consent not to be unreasonably withheld or delayed), except as required by
                    law,
                    any governmental or regulatory authority any court of competent jurisdiction.</p>
                </li>
                <li id="conflict">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Conflict</h3>
                  <p>If there is an inconsistency between any of the provisions in the Channel Partner Authorisation
                    Form
                    and these Channel Partner Authorisation Terms, the provisions of the Channel Partner Authorisation
                    Form shall prevail.</p>
                </li>
                <li id="rights_and_remedies">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Rights and remedies</h3>
                  <p>The rights and remedies provided under the Channel Partner Authorisation Agreement are in
                    addition
                    to, and not exclusive of, any rights or remedies provided by law.</p>
                </li>
                <li id="set_off">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Set-off</h3>
                  <p>All amounts due under the Channel Partner Authorisation Agreement shall be paid in full without
                    any
                    set-off, counterclaim, deduction or withholding (other than any deduction or withholding of tax as
                    required by law).</p>
                </li>
                <li id="interest">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Interest</h3>
                  <p>If a party fails to make a payment due to the other party under the Channel Partner Authorisation
                    Agreement by the due date, then, the defaulting party shall pay interest on the overdue sum from
                    the
                    due date until payment of the overdue sum, whether before or after judgment. Interest under this
                    clause will accrue each day at 4% a year above the Bank of England&apos;s base rate from time to time,
                    but
                    at 4% a year for any period when that base rate is below 0%.</p>
                </li>
                <li id="governing_law">
                  <h3 className="lh-base lead fw-bold mb-2 text-uppercase">Governing law, venue and dispute
                    resolution</h3>
                  <p>Authorised Channel Partner Authorisation Agreement shall be governed and construed in accordance
                    with
                    English law. Any dispute arising under or related to the Channel Partner Authorisation Agreement
                    that
                    is not resolved by good faith discussion among the parties, at their discretion, shall be resolved
                    by
                    binding expedited London Court of International Arbitration (LCIA) arbitration in London with the
                    exception of an action brought in any court having jurisdiction to enforce terms of an arbitration
                    award under this clause or for injunctive relief or, for Licensor at its discretion, where the
                    sole or
                    primary dispute regards payment by Authorised Channel Partner</p>
                </li>
              </ol>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
    ;
};

export default PartnerTermsContent;